import { useMemo, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { ExcelButton } from '../../components/ui/chrome';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../../components/ui/ColumnSearch';

interface WeekCell {
  weekStart: string;
  hours: number;
  capacityHours: number;
  utilisation: number | null;
  overCapacity: boolean;
}

interface SchedulePerson {
  userId: string;
  resource: string;
  scheduledHours: number;
  capacityHours: number;
  capacityPerDayHours: number;
  utilisation: number | null;
  freeHours: number;
  weeks: WeekCell[];
}

interface ScheduleProject {
  projectId: string;
  client: string;
  project: string;
  scheduledHours: number;
  publishedHours: number;
  draftHours: number;
  billableHours: number;
}

interface ScheduleAssignment {
  projectId: string;
  project: string;
  client: string;
  userId: string;
  resource: string;
  task: string;
  start: string;
  end: string;
  hoursPerDay: number;
  hours: number;
  published: boolean;
}

export interface ResourceReport {
  from: string;
  to: string;
  comparedTo: string;
  schedule: {
    summary: {
      scheduledHours: number;
      capacityHours: number;
      utilisation: number | null;
      unallocatedHours: number;
      publishedShare: number | null;
      billableShare: number | null;
      projects: number;
      overbookedPersonWeeks: number;
    };
    byResource: SchedulePerson[];
    byProject: ScheduleProject[];
    weeks: { weekStart: string; weekEnd: string }[];
    detail: { userId: string; client: string; project: string; weekStart: string; hours: number }[];
    assignments: ScheduleAssignment[];
  };
  booked: {
    summary: {
      bookedHours: number;
      scheduledToDateHours: number;
      varianceHours: number;
      variancePct: number | null;
      deliveredVsPlan: number | null;
      unscheduledHours: number;
      absenceHours: number;
    };
    byResource: { userId: string; resource: string; bookedHours: number; scheduledToDateHours: number; varianceHours: number; deliveredVsPlan: number | null }[];
    byProject: { projectId: string; client: string; project: string; bookedHours: number; scheduledToDateHours: number; varianceHours: number; deliveredVsPlan: number | null }[];
  };
  choices: {
    clients: string[];
    projects: { id: string; name: string; client: string }[];
    people: { id: string; name: string }[];
  };
}

const CLIENT_COLORS = ['#e8590c', '#f4a261', '#7c5cff', '#2a9d8f', '#e9c46a', '#4cc9f0', '#90be6d', '#e76f51'];

function hours(value: number): string {
  return value.toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

function percent(value: number | null): string {
  if (value == null) return 'n/a';
  return `${Math.round(value * 100)}%`;
}

function weekLabel(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function rangeLabel(start: string, end: string): string {
  return `${weekLabel(start)} - ${weekLabel(end)}`;
}

function shade(cell: WeekCell): { background: string; color: string } {
  if (cell.overCapacity) return { background: '#9f2d2d', color: '#fff' };
  if (cell.hours <= 0) return { background: 'var(--bo-canvas)', color: 'var(--bo-faint)' };
  const t = Math.min(1, Math.max(0, cell.utilisation ?? 0));
  const red = Math.round(36 + (140 - 36) * t);
  const green = Math.round(24 + (92 - 24) * t);
  const blue = Math.round(72 + (255 - 72) * t);
  return { background: `rgb(${red} ${green} ${blue})`, color: '#f5f3ff' };
}

function personDayLine(people: SchedulePerson[]): string {
  const active = people.filter((person) => person.capacityPerDayHours > 0);
  if (!active.length) return 'scheduled hours';
  const days = active.reduce((sum, person) => sum + person.scheduledHours / person.capacityPerDayHours, 0);
  const rates = [...new Set(active.map((person) => person.capacityPerDayHours))];
  const rounded = Math.round(days);
  return rates.length === 1 ? `${rounded} person-days at ${rates[0]}h` : `${rounded} person-days`;
}

function publishedFigures(summary: ResourceReport['schedule']['summary'], assignments: ScheduleAssignment[]): { share: number | null; draftHours: number } {
  if (summary.publishedShare != null) {
    return {
      share: summary.publishedShare,
      draftHours: Math.round(summary.scheduledHours * (1 - summary.publishedShare) * 100) / 100,
    };
  }
  const draftHours = assignments.filter((row) => !row.published).reduce((sum, row) => sum + row.hours, 0);
  const publishedHours = assignments.filter((row) => row.published).reduce((sum, row) => sum + row.hours, 0);
  const total = draftHours + publishedHours;
  return {
    share: total > 0 ? Math.round((publishedHours / total) * 10000) / 10000 : null,
    draftHours: Math.round(draftHours * 100) / 100,
  };
}

function projectSplit(project: ScheduleProject, assignments: ScheduleAssignment[]): { published: number; draft: number } {
  if (project.publishedHours + project.draftHours > 0) return { published: project.publishedHours, draft: project.draftHours };
  const rows = assignments.filter((row) => row.projectId === project.projectId);
  const published = rows.filter((row) => row.published).reduce((sum, row) => sum + row.hours, 0);
  const draft = rows.filter((row) => !row.published).reduce((sum, row) => sum + row.hours, 0);
  const raw = published + draft;
  if (raw <= 0) return { published: 0, draft: project.scheduledHours };
  return { published: project.scheduledHours * published / raw, draft: project.scheduledHours * draft / raw };
}

function shortName(name: string, names: string[]): string {
  const first = name.split(' ')[0] || name;
  const clashes = names.filter((item) => (item.split(' ')[0] || item) === first);
  return clashes.length > 1 ? name : first;
}

function tone(summary: ResourceReport['schedule']['summary']): { label: string; className: string } {
  if (summary.overbookedPersonWeeks > 0 || (summary.utilisation != null && summary.utilisation > 1)) {
    return { label: 'Over', className: 'bg-red-950 text-red-100' };
  }
  if (summary.utilisation != null && summary.utilisation >= 0.85) {
    return { label: 'Tight', className: 'bg-amber-950 text-amber-100' };
  }
  return { label: 'Headroom', className: 'bg-emerald-950 text-emerald-100' };
}

function Panel({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-3">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-medium">{title}</h2>
        {aside ? <div className="text-xs text-[var(--bo-faint)]">{aside}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function ScheduleOutlook({
  report,
  today,
  periodLabel,
  columnLabel,
  projectId,
  userId,
  updatedAt,
  refreshing,
  onFilter,
  onRefresh,
}: {
  report: ResourceReport;
  today: string;
  periodLabel: string;
  columnLabel: string;
  projectId: string;
  userId: string;
  updatedAt: number;
  refreshing: boolean;
  onFilter: (next: Record<string, string>) => void;
  onRefresh: () => void;
}) {
  const [picked, setPicked] = useState<{ userId: string; weekStart: string } | null>(null);
  const loadSearch = useColumnSearch();
  const gridSearch = useColumnSearch();
  const assignmentSearch = useColumnSearch();
  const schedule = report.schedule;
  const people = useMemo(() => [...schedule.byResource].sort((left, right) => left.resource.localeCompare(right.resource)), [schedule.byResource]);
  const names = people.map((person) => person.resource);
  const published = publishedFigures(schedule.summary, schedule.assignments);
  const load = tone(schedule.summary);
  const resourceCount = people.filter((person) => person.capacityHours > 0).length;
  const pickedRows = schedule.detail.filter((row) => row.userId === picked?.userId && row.weekStart === picked.weekStart);
  const clientSeries = useMemo(() => {
    const namesInOrder: string[] = [];
    const totals = new Map<string, number>();
    for (const row of schedule.detail) {
      const name = row.client || 'No client';
      if (!totals.has(name)) namesInOrder.push(name);
      totals.set(name, (totals.get(name) || 0) + row.hours);
    }
    namesInOrder.sort((left, right) => (totals.get(right) || 0) - (totals.get(left) || 0) || left.localeCompare(right));
    return namesInOrder.map((name, index) => ({
      name,
      color: CLIENT_COLORS[index % CLIENT_COLORS.length],
      hours: schedule.weeks.map((week) => schedule.detail.filter((row) => (row.client || 'No client') === name && row.weekStart === week.weekStart).reduce((sum, row) => sum + row.hours, 0)),
    }));
  }, [schedule.detail, schedule.weeks]);
  const weekCapacity = schedule.weeks.map((week) => people.reduce((sum, person) => sum + (person.weeks.find((cell) => cell.weekStart === week.weekStart)?.capacityHours || 0), 0));
  const matrix = useMemo(() => schedule.byProject.map((project) => people.map((person) => schedule.detail.filter((row) => row.project === project.project && row.client === project.client && row.userId === person.userId).reduce((sum, row) => sum + row.hours, 0))), [people, schedule.byProject, schedule.detail]);
  const loadPeople = people.filter((person) => {
    const cells: Record<string, string> = { resource: person.resource, total: `${hours(person.scheduledHours)}h ${percent(person.utilisation)}` };
    for (const week of schedule.weeks) {
      const cell = person.weeks.find((item) => item.weekStart === week.weekStart);
      cells[week.weekStart] = cell ? `${cell.hours > 0 ? hours(cell.hours) : ''} ${cell.utilisation == null ? '' : percent(cell.utilisation)}` : '';
    }
    return loadSearch.matches(cells);
  });
  const projectRows = schedule.byProject.map((project, rowIndex) => ({ project, values: matrix[rowIndex] })).filter((row) => {
    const cells: Record<string, string> = { project: `${row.project.project} ${row.project.client}`, total: hours(row.values.reduce((sum, value) => sum + value, 0)) };
    people.forEach((person, index) => { cells[person.userId] = row.values[index] > 0 ? hours(row.values[index]) : ''; });
    return gridSearch.matches(cells);
  });
  const assignmentRows = schedule.assignments.filter((row) => assignmentSearch.matches({
    project: row.project,
    resource: row.resource,
    task: row.task || '-',
    dates: rangeLabel(row.start, row.end),
    perDay: hours(row.hoursPerDay),
    hours: hours(row.hours),
    status: row.published ? 'Published' : 'Draft',
  }));
  const snapshot = updatedAt
    ? `Clockify snapshot ${new Date(updatedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
    : 'Clockify';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--bo-faint)]">
        <span>{snapshot}</span>
        <Button size="sm" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh from Clockify
        </Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
        <Kpi label="Scheduled" value={`${hours(schedule.summary.scheduledHours)}h`} detail={personDayLine(people)} />
        <Kpi label="Capacity" value={`${hours(schedule.summary.capacityHours)}h`} detail={`${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}, working days`} />
        <Kpi label="Utilisation" value={percent(schedule.summary.utilisation)} detail={<span className={`rounded-full px-2 py-0.5 ${load.className}`}>{load.label}</span>} />
        <Kpi label="Unallocated" value={`${hours(schedule.summary.unallocatedHours)}h`} detail={`free capacity ${periodLabel}`} />
        <Kpi label="Published" value={percent(published.share)} detail={published.draftHours > 0 ? `${hours(published.draftHours)}h still in draft` : 'Nothing left in draft'} />
        <Kpi label="Billable" value={percent(schedule.summary.billableShare)} detail={`${schedule.summary.projects} ${schedule.summary.projects === 1 ? 'project' : 'projects'}, ${schedule.summary.overbookedPersonWeeks} over-booked person-weeks`} />
      </div>
      <Panel
        title="Team load by week"
        aside={(
          <span className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2">
              Utilisation
              <span className="inline-block h-2 w-24 rounded" style={{ background: 'linear-gradient(to right, rgb(36 24 72), rgb(140 92 255))' }} />
              0 to 100%
            </span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-[#9f2d2d]" /> Over capacity</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm border border-dashed border-[var(--bo-muted)]" /> This week</span>
          </span>
        )}
      >
        <div className="mb-2 flex justify-end">
          <ExcelButton filename="Team load by week" headers={['Resource', ...schedule.weeks.map((week) => weekLabel(week.weekStart)), 'Hours', 'Utilisation']} rows={loadPeople.map((person) => [
            person.resource,
            ...schedule.weeks.map((week) => person.weeks.find((cell) => cell.weekStart === week.weekStart)?.hours || 0),
            person.scheduledHours,
            person.utilisation == null ? '' : person.utilisation,
          ])} />
        </div>
        <div className="overflow-x-auto">
          <table className="bo-cards min-w-full text-xs">
            <thead>
              <tr className="text-left text-[var(--bo-faint)]">
                <th className="sticky left-0 bg-[var(--bo-surface)] px-2 py-2 font-medium">Resource</th>
                {schedule.weeks.map((week) => {
                  const current = today >= week.weekStart && today <= week.weekEnd;
                  return <th key={week.weekStart} className={`px-1 py-2 text-center font-medium ${current ? 'rounded border border-dashed border-[var(--bo-muted)]' : ''}`}>{weekLabel(week.weekStart)}</th>;
                })}
                <th className="px-2 py-2 text-right font-medium"><span className="inline-flex items-center justify-end gap-1">{columnLabel}<ColumnFilterToggle open={loadSearch.open} onClick={loadSearch.toggle} /></span></th>
              </tr>
              {loadSearch.open ? (
                <ColumnFilterInputs
                  columns={[
                    { key: 'resource', label: 'Resource' },
                    ...schedule.weeks.map((week) => ({ key: week.weekStart, label: weekLabel(week.weekStart) })),
                    { key: 'total', label: columnLabel },
                  ]}
                  draft={loadSearch.draft}
                  onChange={loadSearch.set}
                />
              ) : null}
            </thead>
            <tbody>
              {loadPeople.map((person) => (
                <tr key={person.userId}>
                  <td className="sticky left-0 bg-[var(--bo-surface)] px-2 py-1" data-label="">{person.resource}</td>
                  {person.weeks.map((cell) => {
                    const week = schedule.weeks.find((item) => item.weekStart === cell.weekStart);
                    const current = week ? today >= week.weekStart && today <= week.weekEnd : false;
                    const color = shade(cell);
                    const selected = picked?.userId === person.userId && picked.weekStart === cell.weekStart;
                    return (
                      <td key={cell.weekStart} className="px-1 py-1" data-label={weekLabel(cell.weekStart)}>
                        <button
                          type="button"
                          className={`w-full rounded px-1 py-1 text-center ${current ? 'ring-1 ring-dashed ring-[var(--bo-muted)]' : ''} ${selected ? 'ring-2 ring-[#895CF6]' : ''}`}
                          style={{ background: color.background, color: color.color }}
                          onClick={() => setPicked((currentPick) => currentPick?.userId === person.userId && currentPick.weekStart === cell.weekStart ? null : { userId: person.userId, weekStart: cell.weekStart })}
                        >
                          <div>{cell.hours > 0 ? hours(cell.hours) : '·'}</div>
                          <div className="opacity-80">{cell.utilisation == null ? '' : percent(cell.utilisation)}</div>
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 py-1 text-right" data-label={columnLabel}>
                    <div>{hours(person.scheduledHours)}h</div>
                    <div className="text-[var(--bo-faint)]">{percent(person.utilisation)}</div>
                  </td>
                </tr>
              ))}
              <tr className="text-[var(--bo-muted)]">
                <td className="sticky left-0 bg-[var(--bo-surface)] px-2 py-2" data-label="">Team</td>
                {schedule.weeks.map((week, index) => {
                  const scheduled = loadPeople.reduce((sum, person) => sum + (person.weeks[index]?.hours || 0), 0);
                  const capacity = loadPeople.reduce((sum, person) => sum + (person.weeks[index]?.capacityHours || 0), 0);
                  return <td key={week.weekStart} className="px-1 py-2 text-center" data-label={weekLabel(week.weekStart)}>{capacity > 0 ? percent(scheduled / capacity) : ''}</td>;
                })}
                <td className="px-2 py-2 text-right" data-label={columnLabel}>
                  <div>{hours(loadPeople.reduce((sum, person) => sum + person.scheduledHours, 0))}h</div>
                  <div>{percent(loadPeople.reduce((sum, person) => sum + person.capacityHours, 0) > 0 ? loadPeople.reduce((sum, person) => sum + person.scheduledHours, 0) / loadPeople.reduce((sum, person) => sum + person.capacityHours, 0) : null)}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {picked ? (
          <div className="mt-3 border-t border-[var(--bo-border)] pt-3 text-sm">
            <div className="mb-1 font-medium">Week of {weekLabel(picked.weekStart)}</div>
            {pickedRows.length ? pickedRows.map((row) => (
              <div key={`${row.client}-${row.project}`} className="flex justify-between gap-4 py-1">
                <span>{row.client} · {row.project}</span>
                <span>{hours(row.hours)} scheduled</span>
              </div>
            )) : <div className="text-[var(--bo-faint)]">No schedule in this week.</div>}
          </div>
        ) : null}
      </Panel>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="Hours by project" aside={<span><Dot color="#a78bfa" /> Published <Dot color="#5b3fa8" /> Draft</span>}>
          {schedule.byProject.length ? schedule.byProject.map((project) => {
            const split = projectSplit(project, schedule.assignments);
            const widest = Math.max(...schedule.byProject.map((row) => row.scheduledHours), 1);
            const width = (project.scheduledHours / widest) * 100;
            const publishedWidth = project.scheduledHours > 0 ? (split.published / project.scheduledHours) * 100 : 0;
            return (
              <button key={project.projectId} type="button" className="mb-3 block w-full text-left" onClick={() => onFilter({ client: project.client, project: projectId === project.projectId ? '' : project.projectId })}>
                <div className="mb-1 flex justify-between gap-3 text-sm">
                  <span>{project.project}<span className="ml-2 text-xs text-[var(--bo-faint)]">{project.client}</span></span>
                  <span>{hours(project.scheduledHours)}h <span className="text-[var(--bo-faint)]">{percent(schedule.summary.scheduledHours > 0 ? project.scheduledHours / schedule.summary.scheduledHours : null)}</span></span>
                </div>
                <div className="h-3 overflow-hidden rounded bg-[var(--bo-canvas)]">
                  <div className="flex h-full" style={{ width: `${Math.min(100, width)}%` }}>
                    <div style={{ width: `${publishedWidth}%`, background: '#a78bfa' }} />
                    <div style={{ width: `${100 - publishedWidth}%`, background: '#5b3fa8' }} />
                  </div>
                </div>
              </button>
            );
          }) : <div className="text-sm text-[var(--bo-faint)]">No scheduled projects in this period.</div>}
          <p className="text-xs text-[var(--bo-faint)]">Click a project to focus the dashboard on it.</p>
        </Panel>
        <Panel title="Hours by resource" aside={<span><Dot color="#7c5cff" /> Scheduled <span className="ml-2 inline-block h-2 w-4 rounded bg-[var(--bo-canvas)] align-middle" /> Capacity <Dot color="#9f2d2d" /> Over</span>}>
          {people.length ? people.map((person) => {
            const over = person.capacityHours > 0 && person.scheduledHours > person.capacityHours;
            const fill = person.capacityHours > 0 ? Math.min(100, (person.scheduledHours / person.capacityHours) * 100) : (person.scheduledHours > 0 ? 100 : 0);
            const projectsForPerson = new Set(schedule.detail.filter((row) => row.userId === person.userId).map((row) => row.project)).size;
            return (
              <button key={person.userId} type="button" className="mb-3 block w-full text-left" onClick={() => onFilter({ person: userId === person.userId ? '' : person.userId })}>
                <div className="mb-1 flex justify-between gap-3 text-sm">
                  <span>{person.resource}<span className="ml-2 text-xs text-[var(--bo-faint)]">{projectsForPerson} {projectsForPerson === 1 ? 'project' : 'projects'}</span></span>
                  <span className="text-[var(--bo-faint)]">{hours(person.scheduledHours)}/{hours(person.capacityHours)}h</span>
                </div>
                <div className="h-3 overflow-hidden rounded bg-[var(--bo-canvas)]">
                  <div className="h-full" style={{ width: `${fill}%`, background: over ? '#9f2d2d' : '#7c5cff' }} />
                </div>
              </button>
            );
          }) : <div className="text-sm text-[var(--bo-faint)]">No people in this period.</div>}
          <p className="text-xs text-[var(--bo-faint)]">The track is capacity. Purple is scheduled hours. Red means the person is over capacity. Click a person to focus on them.</p>
        </Panel>
      </div>
      <Panel title="Weekly hours by client" aside={<span>{clientSeries.map((client) => <span key={client.name} className="mr-3"><Dot color={client.color} /> {client.name}</span>)}<span className="inline-block w-6 border-t border-dashed border-[var(--bo-muted)] align-middle" /> Capacity</span>}>
        <ClientChart weeks={schedule.weeks.map((week) => week.weekStart)} series={clientSeries} capacity={weekCapacity} />
      </Panel>
      <Panel title="Project × resource" aside={`Scheduled hours ${periodLabel}`}>
        <div className="mb-2 flex justify-end">
          <ExcelButton filename="Project by resource" headers={['Project', 'Client', ...people.map((person) => shortName(person.resource, names)), 'Total']} rows={projectRows.map((row) => [
            row.project.project,
            row.project.client,
            ...row.values,
            row.values.reduce((sum, value) => sum + value, 0),
          ])} />
        </div>
        <div className="overflow-x-auto">
          <table className="bo-cards min-w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bo-border)] text-left text-[var(--bo-faint)]">
                <th className="px-2 py-2 font-medium">Project</th>
                {people.map((person) => <th key={person.userId} className="px-2 py-2 text-right font-medium">{shortName(person.resource, names)}</th>)}
                <th className="px-2 py-2 text-right font-medium"><span className="inline-flex items-center justify-end gap-1">Total<ColumnFilterToggle open={gridSearch.open} onClick={gridSearch.toggle} /></span></th>
              </tr>
              {gridSearch.open ? (
                <ColumnFilterInputs
                  columns={[
                    { key: 'project', label: 'Project' },
                    ...people.map((person) => ({ key: person.userId, label: shortName(person.resource, names) })),
                    { key: 'total', label: 'Total' },
                  ]}
                  draft={gridSearch.draft}
                  onChange={gridSearch.set}
                />
              ) : null}
            </thead>
            <tbody>
              {projectRows.map((row) => {
                const total = row.values.reduce((sum, value) => sum + value, 0);
                return (
                  <tr key={row.project.projectId} className="border-b border-[var(--bo-border)]">
                    <td className="px-2 py-2" data-label="Project">{row.project.project} <span className="text-xs text-[var(--bo-faint)]">{row.project.client}</span></td>
                    {row.values.map((value, column) => <td key={people[column].userId} className="px-2 py-2 text-right" data-label={shortName(people[column].resource, names)}>{value > 0 ? hours(value) : ''}</td>)}
                    <td className="px-2 py-2 text-right" data-label="Total">{hours(total)}</td>
                  </tr>
                );
              })}
              <tr>
                <td className="px-2 py-2 font-medium" data-label="">Total</td>
                {people.map((person, column) => <td key={person.userId} className="px-2 py-2 text-right font-medium" data-label={shortName(person.resource, names)}>{hours(projectRows.reduce((sum, row) => sum + (row.values[column] || 0), 0))}</td>)}
                <td className="px-2 py-2 text-right font-medium" data-label="Total">{hours(projectRows.reduce((sum, row) => sum + row.values.reduce((inner, value) => inner + value, 0), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Panel>
      <details open className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)]">
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Assignments {periodLabel} ({assignmentRows.length})</summary>
        <div className="mb-2 flex justify-end px-3">
          <ExcelButton filename="Assignments" headers={['Project', 'Resource', 'Role / task', 'Dates', 'h/day', 'Hours', 'Status']} rows={assignmentRows.map((row) => [row.project, row.resource, row.task || '', rangeLabel(row.start, row.end), row.hoursPerDay, row.hours, row.published ? 'Published' : 'Draft'])} />
        </div>
        <div className="overflow-x-auto">
          <table className="bo-cards min-w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bo-border)] text-left text-[var(--bo-faint)]">
                <th className="px-3 py-2 font-medium">Project</th>
                <th className="px-3 py-2 font-medium">Resource</th>
                <th className="px-3 py-2 font-medium">Role / task</th>
                <th className="px-3 py-2 font-medium">Dates</th>
                <th className="px-3 py-2 text-right font-medium">h/day</th>
                <th className="px-3 py-2 text-right font-medium">Hours</th>
                <th className="px-3 py-2 font-medium"><span className="inline-flex items-center gap-1">Status<ColumnFilterToggle open={assignmentSearch.open} onClick={assignmentSearch.toggle} /></span></th>
              </tr>
              {assignmentSearch.open ? <ColumnFilterInputs columns={[{ key: 'project', label: 'Project' }, { key: 'resource', label: 'Resource' }, { key: 'task', label: 'Role / task' }, { key: 'dates', label: 'Dates' }, { key: 'perDay', label: 'h/day' }, { key: 'hours', label: 'Hours' }, { key: 'status', label: 'Status' }]} draft={assignmentSearch.draft} onChange={assignmentSearch.set} /> : null}
            </thead>
            <tbody>
              {assignmentRows.map((row) => (
                <tr key={`${row.userId}-${row.projectId}-${row.start}-${row.end}-${row.task}`} className="border-b border-[var(--bo-border)]">
                  <td className="px-3 py-2" data-label="Project">{row.project}</td>
                  <td className="px-3 py-2" data-label="Resource">{row.resource}</td>
                  <td className="px-3 py-2 text-[var(--bo-muted)]" data-label="Role / task">{row.task || '-'}</td>
                  <td className="px-3 py-2" data-label="Dates">{rangeLabel(row.start, row.end)}</td>
                  <td className="px-3 py-2 text-right" data-label="h/day">{hours(row.hoursPerDay)}</td>
                  <td className="px-3 py-2 text-right" data-label="Hours">{hours(row.hours)}</td>
                  <td className="px-3 py-2" data-label="Status">
                    <span className={`rounded px-2 py-0.5 text-xs ${row.published ? 'bg-[#3b2768] text-[#d6c7ff]' : 'border border-[var(--bo-border)] text-[var(--bo-muted)]'}`}>{row.published ? 'Published' : 'Draft'}</span>
                  </td>
                </tr>
              ))}
              {assignmentRows.length ? null : <tr><td className="px-3 py-3 text-[var(--bo-faint)]" colSpan={7}>{schedule.assignments.length ? 'No assignments match.' : 'No assignments in this period.'}</td></tr>}
            </tbody>
          </table>
        </div>
      </details>
      <p className="text-xs text-[var(--bo-faint)]">Hours follow Clockify's daily totals on each person's working days. Capacity is that person's daily capacity times those working days. The assignment list is hours per day times the working days that fall inside the period. A row is published when Clockify's published daily total covers those days.</p>
    </div>
  );
}

function Kpi({ label, value, detail }: { label: string; value: string; detail: ReactNode }) {
  return (
    <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-[var(--bo-faint)]">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-[var(--bo-muted)]">{detail}</div>
    </div>
  );
}

function Dot({ color }: { color: string }) {
  return <span className="mr-1 inline-block h-2 w-2 rounded-sm align-middle" style={{ background: color }} />;
}

function ClientChart({ weeks, series, capacity }: { weeks: string[]; series: { name: string; color: string; hours: number[] }[]; capacity: number[] }) {
  if (!weeks.length) return <div className="text-sm text-[var(--bo-faint)]">No weeks in this period.</div>;
  const width = Math.max(640, weeks.length * 56);
  const height = 220;
  const padL = 32;
  const padB = 36;
  const padT = 8;
  const padR = 8;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const stacked = weeks.map((_, index) => series.reduce((sum, client) => sum + (client.hours[index] || 0), 0));
  const max = Math.max(1, ...stacked, ...capacity);
  const slot = innerW / weeks.length;
  const barW = Math.min(22, slot * 0.55);
  const yOf = (value: number) => padT + innerH - (value / max) * innerH;
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const points = capacity.map((value, index) => `${padL + slot * index + slot / 2},${yOf(value)}`).join(' ');
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="min-w-full" role="img" aria-label="Weekly hours by client">
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={padL} x2={width - padR} y1={yOf(max * tick)} y2={yOf(max * tick)} stroke="var(--bo-border)" />
            <text x={0} y={yOf(max * tick) + 4} fill="var(--bo-faint)" fontSize="10">{hours(max * tick)}</text>
          </g>
        ))}
        {weeks.map((week, index) => {
          let cursor = yOf(stacked[index] || 0);
          const x = padL + slot * index + (slot - barW) / 2;
          return (
            <g key={week}>
              {series.map((client) => {
                const value = client.hours[index] || 0;
                if (value <= 0) return null;
                const barH = (value / max) * innerH;
                const y = cursor;
                cursor += barH;
                return <rect key={client.name} x={x} y={y} width={barW} height={barH} fill={client.color} />;
              })}
              <text x={padL + slot * index + slot / 2} y={height - 8} fill="var(--bo-faint)" fontSize="10" textAnchor="middle">{weekLabel(week)}</text>
            </g>
          );
        })}
        <polyline points={points} fill="none" stroke="var(--bo-muted)" strokeDasharray="4 4" strokeWidth="1.5" />
      </svg>
    </div>
  );
}
